<template>
  <el-card>
    <div slot="header">
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item :to="{ name: $root.getDashboardPath() }">Dashboard</el-breadcrumb-item>
        <el-breadcrumb-item v-if="$root.state.facility" :to="{ name: 'Facility Users' }">{{String($root.state.facilityData.code).toUpperCase()}} Users</el-breadcrumb-item>
        <el-breadcrumb-item v-else :to="{ name: 'Users' }">Users</el-breadcrumb-item>
      </el-breadcrumb>
      <el-button @click="routeNewUser">
        Add Manager
      </el-button>
    </div>
    <el-table :data="managers" v-loading="loading" style="width: 100%">
      <el-table-column
          property="attributes.title"
          label="Title"
      >
      </el-table-column>
        <el-table-column
          property="email"
          label="Email"
          width="200"
      >
      </el-table-column>
      <el-table-column
        label="Full Name"
      >
        <template slot-scope="scope">
          <div>
            {{scope.row.firstName}}  {{scope.row.lastName}}
          </div>
        </template>
      </el-table-column>
      <el-table-column
        label="Locations"
      >
        <template slot-scope="scope" v-if="scope.row.attributes">
            <el-tag :key="tag" v-for="tag in scope.row.attributes.locations" disable-transitions effect="plain" size="mini">
              {{ getLocationName(tag) }}
            </el-tag>
        </template>
      </el-table-column>
      <el-table-column
        property="role.name"
        label="Role"
      >
      </el-table-column>
      <el-table-column
        label="Action"
      >
        <template slot-scope="scope">
          <el-button size="small" @click="() => openDialog(scope.row)" type="primary" icon="el-icon-edit" style="margin-right: 10px"></el-button>
          <el-switch
            v-model="scope.row.enabled"
            active-color="#13ce66"
            inactive-color="#ff4949"
            @change="changeAccountStatus(scope.row)"
          >
          </el-switch>
        </template>
      </el-table-column>
    </el-table>
    <el-dialog :visible.sync="dialogVisible" :before-close="handleClose">
      <EditUser :manager="selectedManager" @update="reload"/>
    </el-dialog>
  </el-card>
</template>

<script>
import axios from 'axios';
import { AUTH_API_BASE, ADMIN_API_BASE } from '../env';
import EditUser from './EditUser.vue';
import getRoles from '../utils/roles';

export default {
  name: 'Users',
  components: { EditUser },
  props: { facility: { type: String } },
  watch: {
    async facility() {
      this.loading = true;
      Promise.all(
        await this.getManagers(),
        await this.getLocations(),
        await this.getRealmRoles(),
      );
      this.loading = false;
    },
  },
  async mounted() {
    this.loading = true;
    Promise.all(
      await this.getManagers(),
      await this.getLocations(),
      await this.getRealmRoles(),
    );
    this.loading = false;
  },
  data() {
    return {
      loading: false,
      dialogVisible: false,
      selectedManager: null,
      managers: [],
      locations: [],
      realmRoles: [],
    };
  },
  methods: {
    async reload() {
      this.dialogVisible = false;
      this.loading = true;
      await this.getManagers();
      await this.getLocations();
      await this.getRealmRoles();
      this.loading = false;
    },
    handleClose(done) {
      this.selectedManager = null;
      done();
    },
    openDialog(manager) {
      this.selectedManager = manager;
      this.dialogVisible = true;
    },
    async getLocations() {
      const response = await axios.get(`${ADMIN_API_BASE}/facilities`, {
        params: {
          limit: 55,
        },
        headers: {
          Authorization: `Bearer ${this.$keycloak.token}`,
        },
      });
      this.locations = response.data;
    },
    async deleteManager(id) {
      this.loading = true;
      try {
        await axios.delete(`${AUTH_API_BASE}/manager/${id}`, {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });
      } catch (err) {
        console.log(err);
      }

      await this.getManagers();
      this.loading = false;
    },
    async changeAccountStatus(manager) {
      try {
        this.loading = true;
        await axios.post(`${AUTH_API_BASE}/manager/${manager.id}`, {
          enabled: manager.enabled,
        },
        {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });
        this.loading = false;

        this.$message.success({
          title: 'Success',
          message: 'Account status changed!',
        });
      } catch (err) {
        this.loading = false;
        this.$message.error({
          title: 'Error',
          message: err.message ? err.message : err.response.data,
        });
      }
    },
    async getManagers() {
      try {
        const response = await axios.get(`${AUTH_API_BASE}/managers`, {
          params: {
            ...(this.facility ? { location: this.facility } : {}),
          },
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });
        this.managers = response.data;

        if (this.managers && this.managers.length > 0) {
          await Promise.all(
            this.managers.map(async (manager, index) => this.getManagerRole(manager.id).then((role) => { this.managers[index].role = role; })),
          );
        }
      } catch (err) {
        console.log(err);
        this.loading = false;
        this.$message.error({
          title: 'Error',
          message: err.response.data,
        });
      }
    },
    async getManagerRole(id) {
      try {
        const { data } = await axios.get(`${AUTH_API_BASE}/manager/role/${id}`, {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });
        const validRoles = getRoles();
        const [foundValidRole] = data.filter(role => (validRoles.includes(role.name) ? role : null));
        return foundValidRole;
      } catch (err) {
        this.$message.error({
          title: 'Error',
          message: err.response.data,
        });
        return null;
      }
    },
    getLocationName(id) {
      const [foundLocation] = this.locations.filter(location => (location._id === id ? location : null));

      if (foundLocation) {
        return foundLocation.title;
      }

      return '-';
    },
    async getRealmRoles() {
      try {
        const response = await axios.get(`${AUTH_API_BASE}/managers/roles`, {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });
        const roles = getRoles();
        this.realmRoles = response.data.filter((role) => {
          if (roles.includes(role.name)) {
            return role;
          }
        });
      } catch (err) {
        this.$message.error({
          title: 'Error',
          message: err.response.data,
        });
      }
    },
    async setLocationForManager(id, locations) {
      await axios.put(`${AUTH_API_BASE}/manager/${id}`, {
        attributes: {
          locations,
        },
      }, {
        headers: {
          Authorization: `Bearer ${this.$keycloak.token}`,
        },
      });
    },
    routeToHomePage() {
      if (this.facility) {
        this.$router.push({ name: 'Facility Dashboard', params: { facility: this.facility } });
        return;
      }
      this.$router.push({ name: 'Dashboard' });
    },
    routeNewUser() {
      if (this.facility) {
        this.$router.push({ name: 'Facility User Form', params: { facility: this.facility } });
        return;
      }
      this.$router.push({ name: 'User Form' });
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/Users.vue